import { EnemyInstance, EnemyIntent } from "../data/enemies";
import { PERKS, PerkId } from "../data/perks";
import { SKILLS, SkillId } from "../data/skills";
import { CombatEvent } from "./combatEvents";
import { calculateDamage } from "./damageCalculation";
import { RunState, cloneRun, getSkillLevel } from "./runState";

export type CombatTurn = "player" | "enemy";

export type CombatAction = {
  kind: "useSkill";
  skillId: SkillId;
  targetIndex?: number;
};

export type CombatActionOption = {
  skillId: SkillId;
  label: string;
  requiresTarget: boolean;
};

export type CombatSnapshot = {
  run: RunState;
  enemies: EnemyInstance[];
  turn: CombatTurn;
  victory: boolean;
  defeat: boolean;
  seed: number;
};

export type CombatResolution = {
  events: CombatEvent[];
  snapshot: CombatSnapshot;
  steps?: CombatStep[];
};

type CombatState = {
  run: RunState;
  enemies: EnemyInstance[];
  turn: CombatTurn;
  rng: SeededRandom;
};

export type CombatStep = {
  event: CombatEvent;
  snapshot: CombatSnapshot;
};

class SeededRandom {
  seed: number;

  constructor(seed: number) {
    this.seed = seed >>> 0;
  }

  next(): number {
    // Mulberry32
    this.seed += 0x6d2b79f5;
    let t = this.seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
}

const cloneEnemy = (enemy: EnemyInstance): EnemyInstance => {
  const instance: EnemyInstance = {
    id: enemy.id,
    name: enemy.name,
    maxHp: enemy.maxHp,
    hp: enemy.hp,
    atk: enemy.atk,
    armor: enemy.armor,
    intent: enemy.intent,
    isDefending: enemy.isDefending,
    defendChance: enemy.defendChance,
    perks: enemy.perks ? [...enemy.perks] : [],
    isAlive: () => instance.hp > 0,
  };
  return instance;
};

const applyDamageToEnemy = (
  state: CombatState,
  targetIndex: number,
  amount: number,
  attackerIndex?: number // Track who dealt the damage for reflection perks
): number | undefined => {
  const enemy = state.enemies[targetIndex];
  if (!enemy || !enemy.isAlive()) {
    return undefined;
  }

  const { finalDamage } = calculateDamage(amount, {
    isDefending: enemy.isDefending,
    armor: enemy.armor,
  });

  const previousHp = enemy.hp;
  enemy.hp = Math.max(0, enemy.hp - finalDamage);
  const actualDamage = previousHp - enemy.hp;

  // Invoke onDamageTaken hooks for enemy perks (with attacker context)
  if (actualDamage > 0 && attackerIndex !== undefined) {
    applyPerkHooksOnDamageTaken(state, enemy.perks, actualDamage, attackerIndex);
  }

  return actualDamage;
};

const applyDamageToPlayer = (
  state: CombatState,
  amount: number,
  isSelfDamage: boolean = false
): number => {
  const { finalDamage } = calculateDamage(amount, {
    isDefending: state.run.player.isDefending,
    armor: state.run.player.armor,
  });

  const previousHp = state.run.player.hp;
  state.run.player.hp = Math.max(0, state.run.player.hp - finalDamage);
  const actualDamage = previousHp - state.run.player.hp;

  // Invoke onDamageTaken hooks for player perks (only on enemy damage, not self-damage)
  if (actualDamage > 0 && !isSelfDamage) {
    applyPerkHooksOnDamageTaken(state, state.run.player.perks, actualDamage, -1); // -1 for "enemy"
  }

  return actualDamage;
};

const applyHealToPlayer = (state: CombatState, amount: number): number => {
  const previousHp = state.run.player.hp;
  state.run.player.hp = Math.min(state.run.player.maxHp, state.run.player.hp + amount);
  return state.run.player.hp - previousHp;
};

const applyHealToEnemy = (
  state: CombatState,
  targetIndex: number,
  amount: number
): number | undefined => {
  const enemy = state.enemies[targetIndex];
  if (!enemy || !enemy.isAlive()) {
    return undefined;
  }

  const previousHp = enemy.hp;
  enemy.hp = Math.min(enemy.maxHp, enemy.hp + amount);
  return enemy.hp - previousHp;
};

const hasPlayerWon = (state: CombatState) => state.enemies.every((enemy) => !enemy.isAlive());

const hasPlayerLost = (state: CombatState) => state.run.player.hp <= 0;

/**
 * Invoke onDamageTaken hooks for all perks on an actor.
 * Counts stacks and calls the hook once per unique perk.
 * @param attackerIndex -1 for "any", or specific enemy index for reflection
 */
const applyPerkHooksOnDamageTaken = (
  state: CombatState,
  perks: PerkId[],
  damageAmount: number,
  attackerIndex: number
): void => {
  const uniquePerks = new Set(perks);
  for (const perkId of uniquePerks) {
    const perk = PERKS[perkId];
    if (!perk?.onDamageTaken) {
      continue;
    }

    const stackCount = perks.filter((p) => p === perkId).length;
    perk.onDamageTaken(damageAmount, {
      stackCount,
      applyHeal: (amount: number) => {
        // Apply healing directly (used for perks like Adrenaline)
        applyHealToPlayer(state, amount);
        return amount;
      },
      applyDamage: (amount: number) => {
        // Apply reflection damage to attacker if valid
        if (attackerIndex >= 0 && attackerIndex < state.enemies.length) {
          const actualDamage = applyDamageToEnemy(state, attackerIndex, amount);
          return actualDamage ?? 0;
        }
        return 0;
      },
      applyAttackBuff: (amount: number) => {
        // This is only called by player perks (when taking damage from enemies)
        state.run.player.atk += amount;
      },
    });

    // Healing is already handled in applyHeal callback above, so no need to process result
  }
};

export class CombatEngine {
  private state: CombatState;

  constructor(options: { run: RunState; enemies: EnemyInstance[]; seed?: number }) {
    this.state = {
      run: cloneRun(options.run),
      enemies: options.enemies.map((enemy) => cloneEnemy(enemy)),
      turn: "player",
      rng: new SeededRandom(options.seed ?? 1),
    };

    this.initializeEnemyIntents();
  }

  getSnapshot(): CombatSnapshot {
    return {
      run: cloneRun(this.state.run),
      enemies: this.state.enemies.map((enemy) => cloneEnemy(enemy)),
      turn: this.state.turn,
      victory: hasPlayerWon(this.state),
      defeat: hasPlayerLost(this.state),
      seed: this.state.rng.seed,
    };
  }

  getAvailableActions(): CombatActionOption[] {
    return Object.keys(this.state.run.player.skills).map((skillId) => ({
      skillId: skillId as SkillId,
      label: SKILLS[skillId as SkillId].name,
      requiresTarget: SKILLS[skillId as SkillId].requiresTarget,
    }));
  }

  getRunState(): RunState {
    return cloneRun(this.state.run);
  }

  isCombatOver(): boolean {
    return hasPlayerWon(this.state) || hasPlayerLost(this.state);
  }

  takePlayerAction(action: CombatAction): CombatResolution {
    if (this.state.turn !== "player" || this.isCombatOver()) {
      return { events: [], snapshot: this.getSnapshot() };
    }

    if (action.kind !== "useSkill") {
      return { events: [], snapshot: this.getSnapshot() };
    }

    // Clear defending state when taking a new action (same as enemies)
    this.state.run.player.isDefending = false;

    const events = this.resolvePlayerSkill(action.skillId, action.targetIndex);
    if (!this.isCombatOver()) {
      this.state.turn = "enemy";
    }
    return { events, snapshot: this.getSnapshot() };
  }

  takeEnemyTurn(): CombatResolution {
    if (this.state.turn !== "enemy" || this.isCombatOver()) {
      return { events: [], snapshot: this.getSnapshot(), steps: [] };
    }

    const events: CombatEvent[] = [];
    const steps: CombatStep[] = [];

    for (let index = 0; index < this.state.enemies.length; index += 1) {
      const enemy = this.state.enemies[index];
      if (!enemy.isAlive()) {
        continue;
      }

      // Execute the enemy's current intent
      if (enemy.intent === "defend") {
        // Emit defend event (isDefending was already set when intent was chosen)
        const event: CombatEvent = {
          kind: "enemyDefend",
          attackerIndex: index,
          armor: enemy.armor,
        };
        events.push(event);
        steps.push({ event, snapshot: this.getSnapshot() });
      } else {
        // Attack - damage is dealt
        const amount = applyDamageToPlayer(this.state, enemy.atk, false);
        const event: CombatEvent = {
          kind: "enemyAttack",
          attackerIndex: index,
          amount,
          ...(amount <= 0 ? { blocked: true } : {}),
        };
        events.push(event);
        steps.push({ event, snapshot: this.getSnapshot() });

        // Invoke onDamageDealt hooks for enemy perks (even if fully blocked)
        this.applyPerkHooksOnDamageDealt(enemy.perks, amount, (healAmount) => {
          const actualHeal = applyHealToEnemy(this.state, index, healAmount);
          if (actualHeal !== undefined && actualHeal > 0) {
            const healEvent: CombatEvent = {
              kind: "enemyHeal",
              attackerIndex: index,
              amount: actualHeal,
            };
            events.push(healEvent);
            steps.push({ event: healEvent, snapshot: this.getSnapshot() });
          }
        });
      }

      // Roll for next intent and sync defending state immediately
      enemy.intent = this.rollEnemyIntent(enemy);
      enemy.isDefending = enemy.intent === "defend";

      if (this.isCombatOver()) {
        break;
      }
    }

    if (!this.isCombatOver()) {
      this.state.turn = "player";
    }

    return { events, snapshot: this.getSnapshot(), steps };
  }

  private resolvePlayerSkill(skillId: SkillId, targetIndex?: number): CombatEvent[] {
    const skill = SKILLS[skillId];
    if (!skill) {
      return [];
    }

    const context = {
      targetIndex,
      skillLevel: getSkillLevel(this.state.run, skillId),
      getPlayerAttack: () => this.state.run.player.atk,
      getPlayerArmor: () => this.state.run.player.armor,
      applyDamageToEnemy: (index: number, amount: number) =>
        applyDamageToEnemy(this.state, index, amount, -1), // -1 indicates player (not an enemy index)
      applyDamageToPlayer: (amount: number) => applyDamageToPlayer(this.state, amount, false),
      applyDamageToPlayerSelf: (amount: number) => applyDamageToPlayer(this.state, amount, true),
      applyHealToPlayer: (amount: number) => applyHealToPlayer(this.state, amount),
      applyAttackBuffToPlayer: (amount: number) => {
        this.state.run.player.atk += amount;
      },
      setPlayerDefending: (defending: boolean) => {
        this.state.run.player.isDefending = defending;
      },
      hasValidTarget: (index: number | undefined) => this.hasValidTarget(index),
      getAliveEnemyIndices: () =>
        this.state.enemies
          .map((enemy, index) => (enemy.isAlive() ? index : -1))
          .filter((index) => index !== -1),
    };

    const events = skill.use(context);

    // Invoke onDamageDealt hooks for player perks
    const playerAttackEvents = events.filter(
      (e): e is Extract<CombatEvent, { kind: "playerAttack" }> => e.kind === "playerAttack"
    );
    const totalDamage = playerAttackEvents.reduce((sum, e) => sum + e.amount, 0);

    if (playerAttackEvents.length > 0) {
      this.applyPerkHooksOnDamageDealt(this.state.run.player.perks, totalDamage, (healAmount) => {
        const actualHeal = applyHealToPlayer(this.state, healAmount);
        if (actualHeal > 0) {
          events.push({ kind: "playerHeal", amount: actualHeal });
        }
      });
    }

    return events;
  }

  /**
   * Invoke onDamageDealt hooks for all perks on an actor.
   * Counts stacks and calls the hook once per unique perk.
   */
  private applyPerkHooksOnDamageDealt(
    perks: PerkId[],
    totalDamage: number,
    applyHeal: (amount: number) => void
  ): void {
    const uniquePerks = new Set(perks);
    for (const perkId of uniquePerks) {
      const perk = PERKS[perkId];
      if (!perk?.onDamageDealt) {
        continue;
      }

      const stackCount = perks.filter((p) => p === perkId).length;
      perk.onDamageDealt(totalDamage, {
        stackCount,
        applyHeal: (amount: number) => {
          applyHeal(amount);
          return amount;
        },
        applyDamage: () => 0, // Not used for onDamageDealt currently
        applyAttackBuff: () => {
          // Not used for onDamageDealt currently
        },
      });
    }
  }

  private rollEnemyIntent(enemy: EnemyInstance): EnemyIntent {
    // Only consider defending if enemy has armor
    if (enemy.armor <= 0 || enemy.defendChance <= 0) {
      return "attack";
    }

    const roll = this.state.rng.next();
    return roll < enemy.defendChance ? "defend" : "attack";
  }

  private initializeEnemyIntents(): void {
    this.state.enemies.forEach((enemy) => {
      const hasPresetDefend = enemy.intent === "defend" || enemy.isDefending;
      if (hasPresetDefend) {
        // Preserve pre-set defending state by syncing the intent flag as well
        enemy.intent = "defend";
        enemy.isDefending = true;
        return;
      }

      enemy.intent = this.rollEnemyIntent(enemy);
      enemy.isDefending = enemy.intent === "defend";
    });
  }

  private hasValidTarget(targetIndex: number | undefined): boolean {
    if (typeof targetIndex !== "number") {
      return false;
    }

    return Boolean(this.state.enemies[targetIndex]?.isAlive());
  }
}
